/**
 * Payment Types for Accounting Module
 *
 * Payment tracking and due date management.
 * ID prefix: pay-xxx for payments, due-xxx for due dates
 */

import type { ISODate, CentsAmount } from '@business-assistant/shared';

/**
 * Payment method
 */
export type PaymentMethod =
  | 'bank_transfer'
  | 'credit_card'
  | 'debit_card'
  | 'cash'
  | 'check'
  | 'paypal'
  | 'stripe'
  | 'sepa_direct_debit'
  | 'other';

/**
 * Payment status
 */
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';

/**
 * Payment entity
 */
export interface Payment {
  id: string; // pay-xxx
  invoiceId: string; // inv-xxx
  bankTransactionId?: string; // btx-xxx (if linked to bank transaction)
  amount: CentsAmount;
  paymentDate: ISODate;
  paymentMethod: PaymentMethod;
  status: PaymentStatus;
  reference?: string; // Payment reference/transaction ID
  notes?: string;
  recordedBy?: string; // usr-xxx
  createdAt: ISODate;
  updatedAt: ISODate;
}

/**
 * Due date status
 */
export type DueDateStatus = 'pending' | 'overdue' | 'paid' | 'partial';

/**
 * Due date entry for calendar/tracking
 */
export interface DueDate {
  id: string; // due-xxx
  invoiceId: string; // inv-xxx
  customerId: string; // cst-xxx
  customerName: string;
  invoiceNumber: string;
  dueDate: ISODate;
  amount: CentsAmount;
  paidAmount: CentsAmount;
  status: DueDateStatus;
  reminderSent: boolean;
  lastReminderDate?: ISODate;
  notes?: string;
}

/**
 * Payment terms
 */
export type PaymentTerms =
  | 'immediate'
  | 'net_7'
  | 'net_15'
  | 'net_30'
  | 'net_45'
  | 'net_60'
  | 'net_90'
  | 'end_of_month'
  | 'custom';

/**
 * Payment creation data
 */
export type PaymentCreateData = Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>;

/**
 * Payment filters
 */
export interface PaymentFilters {
  invoiceId?: string;
  startDate?: ISODate;
  endDate?: ISODate;
  paymentMethod?: PaymentMethod;
  status?: PaymentStatus;
  minAmount?: CentsAmount;
  maxAmount?: CentsAmount;
}

/**
 * Due date filters
 */
export interface DueDateFilters {
  status?: DueDateStatus | DueDateStatus[];
  startDate?: ISODate;
  endDate?: ISODate;
  customerId?: string;
  overdueOnly?: boolean;
}

/**
 * Payment reminder configuration
 */
export interface PaymentReminderConfig {
  enabled: boolean;
  daysBeforeDue: number[]; // e.g., [7, 3, 1] for reminders 7, 3, and 1 day before
  daysAfterDue: number[]; // e.g., [1, 7, 14, 30] for overdue reminders
  emailTemplate?: string;
  maxReminders?: number;
}

/**
 * Get payment method display label
 */
export function getPaymentMethodLabel(method: PaymentMethod): string {
  const labels: Record<PaymentMethod, string> = {
    bank_transfer: 'Bonifico Bancario',
    credit_card: 'Carta di Credito',
    debit_card: 'Carta di Debito',
    cash: 'Contanti',
    check: 'Assegno',
    paypal: 'PayPal',
    stripe: 'Stripe',
    sepa_direct_debit: 'Addebito SEPA',
    other: 'Altro',
  };
  return labels[method] || method;
}

/**
 * Get payment terms display label
 */
export function getPaymentTermsLabel(terms: PaymentTerms): string {
  const labels: Record<PaymentTerms, string> = {
    immediate: 'Immediato',
    net_7: 'Net 7 giorni',
    net_15: 'Net 15 giorni',
    net_30: 'Net 30 giorni',
    net_45: 'Net 45 giorni',
    net_60: 'Net 60 giorni',
    net_90: 'Net 90 giorni',
    end_of_month: 'Fine mese',
    custom: 'Personalizzato',
  };
  return labels[terms] || terms;
}

/**
 * Calculate due date based on payment terms
 */
export function calculateDueDate(issueDate: ISODate, terms: PaymentTerms): ISODate {
  const date = new Date(issueDate);

  switch (terms) {
    case 'immediate':
      break;
    case 'net_7':
      date.setDate(date.getDate() + 7);
      break;
    case 'net_15':
      date.setDate(date.getDate() + 15);
      break;
    case 'net_30':
      date.setDate(date.getDate() + 30);
      break;
    case 'net_45':
      date.setDate(date.getDate() + 45);
      break;
    case 'net_60':
      date.setDate(date.getDate() + 60);
      break;
    case 'net_90':
      date.setDate(date.getDate() + 90);
      break;
    case 'end_of_month':
      date.setMonth(date.getMonth() + 1, 0);
      break;
    default:
      date.setDate(date.getDate() + 30);
  }

  return date.toISOString();
}

/**
 * Get days until/since due date
 */
export function getDaysFromDueDate(dueDate: ISODate): number {
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  const diffTime = due.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Check if invoice is overdue
 */
export function isOverdue(dueDate: ISODate, status: string): boolean {
  if (status === 'paid' || status === 'cancelled') return false;
  return getDaysFromDueDate(dueDate) < 0;
}

/**
 * Get due date status color
 */
export function getDueDateStatusColor(status: DueDateStatus): string {
  const colors: Record<DueDateStatus, string> = {
    pending: '#f59e0b', // Amber
    overdue: '#ef4444', // Red
    paid: '#22c55e',    // Green
    partial: '#3b82f6', // Blue
  };
  return colors[status];
}

/**
 * Get payment status color
 */
export function getPaymentStatusColor(status: PaymentStatus): string {
  const colors: Record<PaymentStatus, string> = {
    pending: '#f59e0b',   // Amber
    completed: '#22c55e', // Green
    failed: '#ef4444',    // Red
    cancelled: '#6b7280', // Gray
    refunded: '#8b5cf6',  // Purple
  };
  return colors[status];
}
